import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { useContent } from "@/lib/api";
import { Hl } from "@/pages/Rules";
import { PageHero, Reveal, Skeleton, Wrap } from "@/night/ui";

function Results({ rules, q }) {
  const hits = useMemo(() => {
    const out = [];
    const l = q.toLowerCase();
    rules.forEach((r) => r.sections.forEach((s) => s.items.forEach((it) => { if (it.toLowerCase().includes(l) || s.title.toLowerCase().includes(l)) out.push({ r, s, it }); })));
    return out.slice(0, 60);
  }, [rules, q]);
  if (!hits.length) return <p className="border border-[var(--n-line)] p-10 text-center text-slate-400" data-testid="rules-search-empty">ما لقينا شي يطابق "{q}"</p>;
  return (
    <div data-testid="rules-search-results">
      <p className="f-mono mb-4 text-xs text-slate-500">{hits.length} RESULTS</p>
      <div className="divide-y divide-[var(--n-line)] border border-[var(--n-line)]">
        {hits.map(({ r, s, it }, i) => (
          <Link key={i} to={`/rules/${r.slug}?q=${encodeURIComponent(q)}`} className="block p-6 transition-colors hover:bg-[var(--n-ink)]" data-testid="rules-search-hit">
            <div className="text-xs text-[var(--n-cyan)]">{r.name} · <Hl text={s.title} q={q} /></div>
            <p className="mt-2 leading-8 text-slate-200"><Hl text={it} q={q} /></p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Rules() {
  const { data = [], isLoading } = useContent("rules");
  const [q, setQ] = useState("");
  return (
    <>
      <PageHero en="THE LAW" ar="دستور المدينة" desc="دخولك للمدينة يعني موافقتك على جميع القوانين. ابحث عن أي قانون بكلمة وحدة.">
        <div className="relative mt-10 max-w-2xl">
          <Search size={20} className="absolute right-5 top-1/2 -translate-y-1/2 text-[var(--n-cyan)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث في كل القوانين... مثال: RDM، سرقة، باند" className="field !py-5 !pr-14 !text-lg" data-testid="rules-search-input" />
        </div>
      </PageHero>
      {isLoading ? <Skeleton /> : (
        <Wrap className="mt-16">
          {q.trim().length > 1 ? <Results rules={data} q={q.trim()} /> : (
            <div className="grid border-r border-t border-[var(--n-line)] sm:grid-cols-2 lg:grid-cols-3">
              {data.map((r, i) => (
                <Reveal key={r.slug} delay={(i % 3) * 0.06} className="border-b border-l border-[var(--n-line)]">
                  <Link to={`/rules/${r.slug}`} className="group relative block h-full overflow-hidden p-8 transition-colors hover:bg-[var(--n-ink)]" data-testid={`rules-cat-${r.slug}`}>
                    <span className="f-lat absolute -left-2 -top-4 text-8xl leading-none text-white/[0.03] transition-colors group-hover:text-[var(--n-cyan)]/10">{String(i + 1).padStart(2, "0")}</span>
                    <span className="kicker">{r.tag}</span>
                    <h3 className="f-ar mt-4 text-2xl leading-snug">{r.name}</h3>
                    <p className="mt-3 line-clamp-2 text-sm leading-7 text-[var(--n-mute)]">{r.summary}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[var(--n-cyan)] transition-transform group-hover:-translate-x-2">اقرأ <ArrowLeft size={15} /></span>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </Wrap>
      )}
    </>
  );
}
